import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import InvoiceCard from './InvoiceCard';
import ComplianceCard from './ComplianceCard';
import LastChanged from './LastChanged';
import PartForm, { SelectedRecipes, ATTENTION_WORDS, ATTENTION_HOURS, PackagingLayersView, COUNT_WORDS, PACK_PLACE_WORDS, packagingChain } from './forms/PartForm';

function parseReq(val) {
  if (!val) return [];
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : [val]; }
  catch { return [val]; }
}

// Single source of truth for the part detail view.
// `scope` controls which sections render:
//   - 'worker' : shop-floor view -- everything, read-only where Admin edits
//   - 'full'   : admin view (everything, incl. config + future financial/stock)
export default function PartDetail({ partId, onBack, scope = 'full' }) {
  const { user } = useAuth();
  const isFull = scope === 'full';

  const [part, setPart]       = useState(null);
  const [machines, setMachines] = useState([]);
  const [colours, setColours]   = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  function loadPart() {
    Promise.all([
      api.get(`/api/v1/parts/${partId}`),
      api.get('/api/v1/machines').catch(() => ({ data: [] })),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
      api.get('/api/v1/packaging-containers').catch(() => ({ data: [] })),
    ]).then(([p, m, cl, pc]) => {
      setContainers(pc.data || []);
      setPart(p.data);
      setMachines(m.data);
      setColours(cl.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { loadPart(); }, [partId]); // eslint-disable-line

  if (loading) return <div style={s.center}>Loading...</div>;
  if (!part)   return <div style={s.center}>Part not found</div>;

  const machineName = id => machines.find(m => m.id === id)?.name || `#${id}`;
  const secondaryIds = part.secondary_machine_ids || [];
  const inProduct = (part.product_parts || []).length > 0;
  const colourHex = part.colour ? colours.find(c => c.name === part.colour)?.hex : null;

  return (
    <div style={s.page}>
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <button onClick={onBack} style={s.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Parts
        </button>
        {part.parent && (
          <>
            <span style={s.breadcrumbSep}>·</span>
            <span style={s.breadcrumbItem}>{part.parent.name}</span>
          </>
        )}
        <span style={s.breadcrumbSep}>·</span>
        <span style={{ ...s.breadcrumbItem, color: '#f0f2f5' }}>{part.name}</span>
      </div>

      {/* Header */}
      <div style={s.detailHeader}>
        <div>
          {part.parent && (
            <div style={s.parentLink}>
              <i className="fa-solid fa-arrow-up" style={{ fontSize: '10px', marginRight: '4px' }} />
              Part of: {part.parent.name}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={s.detailRef}>{part.part_number || '—'}</div>
            {part.colour && (() => {
              return <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#9ca3af' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: colourHex || '#6b7280', border: `1px solid ${colourHex ? 'rgba(255,255,255,0.15)' : '#2a3040'}`, flexShrink: 0 }} />
                {part.colour}
              </div>;
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h1 style={s.detailName}>{part.name}</h1>
            {part.is_critical && (
              <span style={{ fontSize: '10px', background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', borderRadius: '4px', padding: '2px 8px', fontWeight: '700', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '9px' }} /> Critical
              </span>
            )}
            {part.status === 'discontinued' && (
              <span style={{ fontSize: '10px', background: '#78350f', color: '#fcd34d', border: '1px solid #b45309', borderRadius: '4px', padding: '2px 8px', fontWeight: '700', flexShrink: 0 }}>
                Discontinued{part.discontinued_date ? ` · ${new Date(part.discontinued_date).toLocaleDateString()}` : ''}
              </span>
            )}
          </div>
          {part.customer && <div style={s.detailCustomer}>{part.customer}</div>}
        </div>
        {user && (
          <button style={s.editBtn} onClick={() => setShowEdit(true)}>
            <i className="fa-solid fa-pen" style={{ marginRight: '6px' }} />Edit
          </button>
        )}
      </div>

      {isFull && <LastChanged type="part" id={part.id} refresh={part.updated_at} style={{ margin: '-6px 0 16px' }} />}

      {/* Cards grid */}
      <div style={s.cardsGrid}>
        {/* The same groups, in the same order, as the part form (29 Sep).
            One grid read left to right, so the order holds at any width and
            the cards in a row share a height. Identity is the header above. */}

        {/* Moulding */}
        <div style={s.card}>
          <div style={s.cardTitle}>Moulding</div>
          <InfoRow label="Primary machine"   value={part.primary_machine ? `${part.primary_machine.display_name || part.primary_machine.name}${part.primary_machine.tonnes ? ` · ${part.primary_machine.tonnes}T` : ''}` : '—'} />
          {secondaryIds.map((id, idx) => (
            <InfoRow key={id} label={idx === 0 ? 'Secondary machine' : idx === 1 ? 'Tertiary machine' : `Backup ${idx + 1}`} value={machineName(id)} />
          ))}
          <InfoRow label="Mould" value={part.mould ? `${part.mould.ref} — ${part.mould.name}${part.mould.cavities ? ` (${part.mould.cavities} cav.)` : ''}` : '—'} />
          <div style={s.statStrip}>
            <Stat label="Cycle"  value={part.target_cycle_s != null ? `${part.target_cycle_s} s` : null} />
            <Stat label="Shot"   value={part.shot_weight_g  != null ? `${part.shot_weight_g} g`  : null} />
            <Stat label="Part"   value={part.part_weight_g  != null ? `${part.part_weight_g} g`  : null} />
            <Stat label="Sprue"  value={part.sprue_weight_g != null ? `${part.sprue_weight_g} g` : null} />
            <Stat label="Scrap"  value={part.scrap_pct      != null ? `${part.scrap_pct}%`       : null} />
          </div>
        </div>

        {/* Material: each recipe spelled out, as on the form */}
        <div style={s.card}>
          <div style={s.cardTitle}>Material</div>
          <SelectedRecipes colours={colours}
            recipes={[...(part.part_recipes || [])].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)).map(pr => pr.recipe).filter(Boolean)}
            emptyText="No recipe linked yet. Add one with Edit → Material." />
        </div>

        {/* At the machine */}
        <div style={s.card}>
          <div style={s.cardTitle}>At the machine</div>
          <InfoRow label="Attention while running" value={part.attention_level != null
            ? <span>{part.attention_level} · {ATTENTION_WORDS[part.attention_level] || ''} <span style={{ color: '#6b7280', fontWeight: 400 }}>({ATTENTION_HOURS[part.attention_level]})</span></span>
            : <span style={{ color: '#f59e0b' }}>Not estimated</span>} />
          <InfoRow label="Collector box"     value={part.collector_box_qty != null ? `${part.collector_box_qty.toLocaleString()} pcs` : '—'} />
          <ReqSection label="Handling"     value={part.handling_requirements} last />
        </div>

        {/* Quality */}
        <div style={s.card}>
          <div style={s.cardTitle}>Quality</div>
          <InfoRow label="Classification" value={part.is_critical
            ? <span style={{ color: '#fca5a5', fontWeight: 600 }}><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '5px', fontSize: '11px' }} />Critical / safety part</span>
            : 'Standard'} />
          <ReqSection label="Dimensional"  value={part.dim_requirements} />
          <ReqSection label="Visual"       value={part.visual_requirements} />
          <InfoRow label="Weight log interval" value={part.weight_log_interval_minutes != null ? `${part.weight_log_interval_minutes} min` : '—'} />
        </div>

        {/* Product & assembly */}
        <div style={s.card}>
          <div style={s.cardTitle}>Product & assembly</div>
          <ProductsTab partId={partId} readOnly={!isFull} containers={containers} />
          <ReqSection label="Assembly"     value={part.assembly_requirements} last />
        </div>

        {/* Packaging -- or, for a part that goes into a product, how it is
            kept until assembly (the product is what gets packed) */}
        <div style={s.card}>
          <div style={s.cardTitle}>{inProduct ? 'Stored until assembly' : 'Packaging'}</div>
          <PackagingLayersView layers={part.packaging_layers} containers={containers} />
          {!inProduct && <InfoRow label="How the count is checked" value={COUNT_WORDS[part.packing_count_method] || '—'} />}
          {!inProduct && <InfoRow label="Where it is packed" value={PACK_PLACE_WORDS[part.packing_place] || '—'} />}
          {part.packaging_requirements && <ReqSection label="Packaging notes" value={part.packaging_requirements} plain last />}
        </div>

        {/* Directors only: renders nothing for anyone else (15 Oct 2026). */}
        <InvoiceCard kind="part" id={part.id} ownName={part.name} number={part.part_number} cardStyle={s.card} titleStyle={s.cardTitle} />
        <ComplianceCard kind="part" id={part.id} cardStyle={s.card} titleStyle={s.cardTitle} />

        {/* Notes, across the whole width, when there are any */}
        {part.description && (
          <div style={{ ...s.card, gridColumn: '1 / -1' }}>
            <div style={s.cardTitle}>Notes</div>
            <div style={{ fontSize: '13px', color: '#d1d5db', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{part.description}</div>
          </div>
        )}
      </div>

      {/* Process — full width */}
      <div style={{ ...s.card, marginBottom: '16px' }}>
        <div style={s.cardTitle}>Process Parameters</div>
        <ProcessTab part={part} />
      </div>

      {/* History — full width */}
      <div style={{ ...s.card, marginBottom: '16px' }}>
        <div style={s.cardTitle}>Production History</div>
        <HistoryTab part={part} />
      </div>

      {/* Variants */}
      {part.variants && part.variants.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={s.sectionTitle}>
            <i className="fa-solid fa-layer-group" style={{ marginRight: '8px' }} />
            Variants ({part.variants.length})
          </div>
          <div style={s.varGrid}>
            {part.variants.map(v => (
              <div key={v.id} style={s.varCard} onClick={() => { setPart(null); setLoading(true); api.get(`/api/v1/parts/${v.id}`).then(r => { setPart(r.data); setLoading(false); }); }}>
                <div style={s.varRef}>{v.part_number || '—'}</div>
                <div style={s.varName}>{v.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit modal — shared PartForm */}
      {showEdit && (
        <PartForm
          part={part}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); loadPart(); }}
        />
      )}
    </div>
  );
}

// ── SHARED ROWS ───────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <div style={s.infoRow}>
      <span style={s.infoLabel}>{label}</span>
      <span style={s.infoValue}>{value || '—'}</span>
    </div>
  );
}

// One number in the Moulding card's strip: cycle, weights, scrap.
function Stat({ label, value }) {
  return (
    <div style={s.statTile}>
      <div style={{ ...s.statTileVal, color: value ? '#f0f2f5' : '#4b5563' }}>{value || '—'}</div>
      <div style={s.statTileLabel}>{label}</div>
    </div>
  );
}

function ReqSection({ label, value, plain, last }) {
  const chips = plain ? null : parseReq(value);
  return (
    <div style={last ? { ...s.reqSection, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 } : s.reqSection}>
      <div style={s.reqLabel}>{label}</div>
      {plain ? (
        <div style={s.reqValue}>{value || <span style={{ color: '#4b5563' }}>Not specified</span>}</div>
      ) : chips && chips.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
          {chips.map(c => (
            <span key={c} style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', color: '#9ca3af' }}>{c}</span>
          ))}
        </div>
      ) : (
        <span style={{ color: '#4b5563', fontSize: '13px' }}>Not specified</span>
      )}
    </div>
  );
}

// ── PROCESS TAB ───────────────────────────────────────────────
function ProcessTab({ part }) {
  const [state, setState]   = useState('loading');
  const [params, setParams] = useState(null);
  const [optMeta, setOptMeta] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!part.mould_id) { setState('no-mould'); return; }
    api.get(`/api/v1/optimisations?mould_id=${part.mould_id}`)
      .then(r => {
        if (!r.data.length) { setState('no-golden'); return null; }
        const best = r.data.find(o => o.has_golden) || r.data[0];
        return api.get(`/api/v1/optimisations/${best.id}`);
      })
      .then(r => {
        if (!r) return;
        const opt = r.data;
        const versions = opt.versions || [];
        const goldenV  = versions.find(v => v.is_golden);
        const latestV  = versions[versions.length - 1];
        const displayV = goldenV || latestV;
        if (!displayV?.params) { setState('no-golden'); return; }
        setParams(displayV.params);
        setOptMeta({
          id:             opt.id,
          machine:        opt.machine,
          recipe:         opt.recipe,
          version_number: displayV.version_number,
          is_golden:      !!goldenV,
          created_at:     displayV.created_at,
        });
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [part.mould_id]);

  if (state === 'no-mould') return (
    <div style={s.emptyTab}>
      <i className="fa-solid fa-box" style={{ fontSize: '32px', color: '#2a3040', marginBottom: '12px', display: 'block' }} />
      <div>Link a mould to this part to see process parameters.</div>
    </div>
  );
  if (state === 'loading') return <div style={s.emptyTab}>Loading...</div>;
  if (state === 'no-golden') return (
    <div style={s.emptyTab}>
      <i className="fa-solid fa-sliders" style={{ fontSize: '32px', color: '#2a3040', marginBottom: '12px', display: 'block' }} />
      <div>Process parameters will appear here once a Setup has been completed for this mould.</div>
      <button style={{ ...s.addBtn, marginTop: '16px' }} onClick={() => window.location.href = '/process'}>Go to Process Setup</button>
    </div>
  );
  if (state === 'error' || !params) return (
    <div style={s.emptyTab}><div style={{ color: '#ef4444' }}>Could not load process parameters.</div></div>
  );

  const checks = params.checks || {};

  return (
    <div>
      <div style={ps.goldenBanner}>
        {optMeta?.is_golden
          ? <i className="fa-solid fa-star" style={{ color: '#f59e0b', marginRight: '8px' }} />
          : <i className="fa-solid fa-sliders" style={{ color: '#14b8a6', marginRight: '8px' }} />}
        <span style={{ fontSize: '12px', fontWeight: '700', color: optMeta?.is_golden ? '#f59e0b' : '#14b8a6' }}>
          {optMeta?.is_golden ? 'Golden Run' : 'Latest Version'}
        </span>
        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
          V{optMeta?.version_number} · {optMeta?.machine?.name} · {optMeta?.recipe?.name}
          {optMeta?.created_at ? ` · ${new Date(optMeta.created_at).toLocaleDateString()}` : ''}
        </span>
        <button style={ps.setupBtn} onClick={() => window.location.href = `/process/optimise/${optMeta?.id}`}>
          Open Workspace <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '10px', marginLeft: '4px' }} />
        </button>
      </div>

      <PSection title="Temperatures">
        <div style={ps.zoneRow}>
          {(params.zones || []).map((z, i) => (
            <div key={i} style={ps.zoneBox}>
              <div style={ps.zoneTemp}>{z.temp}°C</div>
              <div style={ps.zoneLabel}>{z.label}</div>
            </div>
          ))}
          <div style={ps.zoneBox}>
            <div style={{ ...ps.zoneTemp, color: '#6b7280' }}>{params.mould_temp}°C</div>
            <div style={ps.zoneLabel}>Mould</div>
          </div>
        </div>
        {params.melt_temp_window && (
          <PRow label="Estimated melt" value={`~${params.melt_temp_estimated}°C`} note={`window ${params.melt_temp_window.min}–${params.melt_temp_window.max}°C`} pass={checks.melt_in_window?.pass} />
        )}
      </PSection>

      <PSection title="Injection">
        <div style={ps.grid2}>
          <PRow label="Fill time"      value={params.fill_time_s   != null ? `${params.fill_time_s}s`   : '—'} />
          <PRow label="Inj pressure"   value={params.inj_pressure_bar != null ? `${params.inj_pressure_bar} bar` : '—'} />
          <PRow label="V→P switchover" value={params.vp_value || '—'} />
          <PRow label="Shot volume"    value={params.shot_vol_cm3  != null ? `${params.shot_vol_cm3} cm³`  : '—'} />
          <PRow label="Dosing stroke"  value={params.dosing_stroke_mm != null ? `${params.dosing_stroke_mm} mm` : '—'} />
          {params.shear_rate != null && <PRow label="Gate shear" value={`${params.shear_rate.toLocaleString()} s⁻¹`} pass={checks.gate_shear_rate?.pass} />}
        </div>
        {params.inj_stages?.length > 0 && (
          <div style={ps.stageTable}>
            {params.inj_stages.map(st => (
              <div key={st.stage} style={ps.stageRow}>
                <span style={ps.stageBadge}>S{st.stage}</span>
                <span style={ps.stageVal}>{st.speed_pct}%</span>
                <span style={ps.stageMeta}>{st.pressure_bar} bar</span>
                <span style={{ ...ps.stageMeta, marginLeft: 'auto' }}>until {st.until_mm}mm</span>
              </div>
            ))}
          </div>
        )}
      </PSection>

      <PSection title="Hold / Pack">
        <div style={ps.grid2}>
          <PRow label="Gate freeze" value={params.gate_freeze_s != null ? `${params.gate_freeze_s}s` : '—'} />
          <PRow label="Hold total"  value={params.hold_total_s  != null ? `${params.hold_total_s}s`  : '—'} pass={checks.hold_vs_gate_freeze?.pass} />
          <PRow label="Suckback"    value={params.suckback_mm   != null ? `${params.suckback_mm} mm` : '—'} />
        </div>
        {params.hold_stages?.length > 0 && (
          <div style={ps.stageTable}>
            {params.hold_stages.map(st => (
              <div key={st.stage} style={ps.stageRow}>
                <span style={ps.stageBadge}>S{st.stage}</span>
                <span style={ps.stageVal}>{st.pressure_bar} bar</span>
                <span style={ps.stageMeta}>{st.time_s}s</span>
              </div>
            ))}
          </div>
        )}
      </PSection>

      <PSection title="Cooling">
        <div style={ps.grid2}>
          <PRow label="Min cooling"  value={params.cooling_min_s != null ? `${params.cooling_min_s}s` : '—'} />
          <PRow label="Recommended"  value={params.cooling_rec_s != null ? `${params.cooling_rec_s}s` : '—'} />
          <PRow label="Flow rate"    value={params.flow_rate_l_min != null ? `${params.flow_rate_l_min} l/min per circuit` : '—'} />
          <PRow label="Recovery"     value={params.recovery_time_s != null ? `${params.recovery_time_s}s` : '—'} pass={checks.recovery_vs_cooling?.pass} />
        </div>
      </PSection>

      <PSection title="Plasticising">
        <div style={ps.grid2}>
          <PRow label="Screw speed"   value={params.screw_speed_rpm   != null ? `${params.screw_speed_rpm} rpm`   : '—'} note={params.screw_speed_overlap} />
          <PRow label="Back pressure" value={params.back_pressure_bar  != null ? `${params.back_pressure_bar} bar`  : '—'} note={params.back_pressure_overlap} />
          <PRow label="Shot/barrel"   value={params.shot_barrel_ratio  != null ? `${params.shot_barrel_ratio}%`    : '—'} pass={checks.shot_barrel_ratio?.pass} />
          {params.residence_time_s != null && <PRow label="Residence time" value={`${params.residence_time_s}s`} pass={checks.residence_time?.pass} />}
        </div>
      </PSection>

      {params.clamp_force_kn != null && (
        <PSection title="Clamp">
          <div style={ps.grid2}>
            <PRow label="Min clamp" value={`${params.clamp_min_kn} kN`} />
            <PRow label="Suggested" value={`${params.clamp_force_kn} kN`} />
          </div>
        </PSection>
      )}

      <PSection title="Cycle Summary">
        <div style={ps.cycleRow}>
          {[
            { label: 'Fill',     val: params.fill_time_s,     color: '#6366f1' },
            { label: 'Hold',     val: params.hold_total_s,    color: '#f59e0b' },
            { label: 'Cool',     val: params.cooling_rec_s,   color: '#22c55e' },
            { label: 'Recovery', val: params.recovery_time_s, color: '#14b8a6' },
          ].map(item => (
            <div key={item.label} style={ps.cycleItem}>
              <div style={{ ...ps.cycleVal, color: item.color }}>{item.val != null ? `${item.val}s` : '—'}</div>
              <div style={ps.cycleLabel}>{item.label}</div>
            </div>
          ))}
        </div>
      </PSection>
    </div>
  );
}

function PSection({ title, children }) {
  return (
    <div style={ps.section}>
      <div style={ps.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function PRow({ label, value, note, pass }) {
  return (
    <div style={ps.prow}>
      <span style={ps.prowLabel}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={ps.prowValue}>{value}</span>
        {note && <span style={ps.prowNote}>{note}</span>}
        {pass === true  && <i className="fa-solid fa-circle-check" style={{ color: '#22c55e', fontSize: '11px' }} />}
        {pass === false && <i className="fa-solid fa-circle-xmark" style={{ color: '#ef4444', fontSize: '11px' }} />}
      </span>
    </div>
  );
}

// ── HISTORY TAB ───────────────────────────────────────────────
function HistoryTab({ part }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!part.mould_id) { setLoading(false); return; }
    api.get(`/api/v1/jobs/archive?mould_id=${part.mould_id}`)
      .then(r => { setJobs(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [part.mould_id]);

  if (!part.mould_id) return (
    <div style={s.emptyTab}>
      <i className="fa-solid fa-box" style={{ fontSize: '32px', color: '#2a3040', marginBottom: '12px', display: 'block' }} />
      <div>Link a mould to this part to see production history.</div>
    </div>
  );
  if (loading) return <div style={s.emptyTab}>Loading...</div>;
  if (jobs.length === 0) return (
    <div style={s.emptyTab}>
      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '32px', color: '#2a3040', marginBottom: '12px', display: 'block' }} />
      <div>Production history will appear here once jobs are run.</div>
    </div>
  );

  const goldenCount = jobs.filter(j => j.golden_run).length;

  return (
    <div>
      <div style={hs.statsRow}>
        <div style={hs.statBox}>
          <div style={hs.statVal}>{jobs.length}</div>
          <div style={hs.statLabel}>Total runs</div>
        </div>
        <div style={hs.statBox}>
          <div style={{ ...hs.statVal, color: '#f59e0b' }}>{goldenCount}</div>
          <div style={hs.statLabel}>Golden runs</div>
        </div>
        <div style={hs.statBox}>
          <div style={{ ...hs.statVal, fontSize: '14px' }}>{new Date(jobs[0].started_at).toLocaleDateString()}</div>
          <div style={hs.statLabel}>Last run</div>
        </div>
      </div>
      <div style={{ marginTop: '12px' }}>
        {jobs.map(job => {
          const start  = new Date(job.started_at);
          const end    = job.ended_at ? new Date(job.ended_at) : null;
          const durMin = end ? Math.round((end - start) / 60000) : null;
          return (
            <div key={job.id} style={hs.jobRow}>
              <div style={hs.jobDate}>
                <div style={hs.jobDateMain}>{start.toLocaleDateString()}</div>
                <div style={hs.jobDateSub}>{start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div>
                <div style={hs.jobMachine}>{job.machines?.name}</div>
                <div style={hs.jobMaterial}>{job.material?.name}</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {durMin !== null && <span style={hs.jobDur}>{durMin}m</span>}
                {job.golden_run && (
                  <span style={hs.goldenBadge}>
                    <i className="fa-solid fa-star" style={{ fontSize: '8px', marginRight: '3px' }} />
                    Golden
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PRODUCTS TAB ─────────────────────────────────────────────
// Which products the part goes into. Read-only on the shop-floor view (29 Sep:
// the floor needs to see it; changing it stays in Admin).
function ProductsTab({ partId, readOnly = false, containers = [] }) {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState('');

  function load() {
    api.get('/api/v1/products')
      .then(r => { setAllProducts(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [partId]); // eslint-disable-line

  const memberProducts = allProducts.filter(prod =>
    (prod.product_parts || []).some(pp => pp.part?.id === parseInt(partId))
  );
  const memberIds = new Set(memberProducts.map(p => p.id));

  function removeFromProduct(prod) {
    setSaving(true);
    api.delete(`/api/v1/products/${prod.id}/parts/${partId}`)
      .then(() => load())
      .finally(() => setSaving(false));
  }

  function addToProduct(prod) {
    setSaving(true);
    api.post(`/api/v1/products/${prod.id}/parts`, { part_id: partId, qty_per_assembly: 1 })
      .then(() => { load(); setSearch(''); })
      .finally(() => setSaving(false));
  }

  const q = search.toLowerCase();
  const available = search
    ? allProducts.filter(p =>
        !memberIds.has(p.id) &&
        ((p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q))
      ).slice(0, 6)
    : [];

  if (loading) return <div style={s.emptyTab}>Loading...</div>;

  return (
    <div style={{ marginBottom: '16px' }}>
      {memberProducts.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fa-solid fa-layer-group" style={{ color: '#4b5563', fontSize: '12px' }} />
          Not in any product yet
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          {memberProducts.map(prod => {
            const pp = (prod.product_parts || []).find(pp => pp.part?.id === parseInt(partId));
            return (
              <div key={prod.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', marginBottom: '8px' }}>
                <i className="fa-solid fa-layer-group" style={{ color: '#14b8a6', fontSize: '13px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{prod.name}</div>
                  <div style={{ fontSize: '10px', color: '#14b8a6', fontFamily: 'monospace' }}>{prod.code}</div>
                  {/* How the finished product is packed (051) */}
                  <div style={{ fontSize: '11px', color: prod.packaging_layers ? '#9ca3af' : '#6b7280', marginTop: '3px', lineHeight: 1.4 }}>
                    {prod.packaging_layers ? <>Packed: {packagingChain(prod.packaging_layers, containers)}</> : 'Packaging not entered for this product yet'}
                  </div>
                </div>
                {pp && <span style={{ fontSize: '11px', color: '#6b7280' }}>×{pp.qty_per_assembly}</span>}
                {!readOnly && <button
                  onClick={() => removeFromProduct(prod)}
                  disabled={saving}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '2px 4px', opacity: saving ? 0.5 : 1 }}>
                  <i className="fa-regular fa-trash-can" />
                </button>}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
      <div style={{ borderTop: memberProducts.length > 0 ? '1px solid #2a3040' : 'none', paddingTop: memberProducts.length > 0 ? '14px' : '0' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '8px' }}>
          Add to product
        </div>
        <div style={{ position: 'relative' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            style={{ width: '100%', boxSizing: 'border-box', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 12px', fontSize: '13px', fontFamily: 'system-ui, sans-serif' }}
          />
          {search && available.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e2329', border: '1px solid #2a3040', borderRadius: '6px', zIndex: 10, marginTop: '2px', maxHeight: '200px', overflowY: 'auto' }}>
              {available.map(p => (
                <div key={p.id}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #2a3040', fontSize: '13px' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                  onClick={() => addToProduct(p)}>
                  <span style={{ fontWeight: '600' }}>{p.name}</span>
                  {p.code && <span style={{ fontSize: '11px', color: '#14b8a6', fontFamily: 'monospace', marginLeft: '8px' }}>{p.code}</span>}
                </div>
              ))}
            </div>
          )}
          {search && available.length === 0 && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
              {allProducts.filter(p => !memberIds.has(p.id)).length === 0
                ? 'All products already contain this part'
                : 'No matching products'}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const ps = {
  goldenBanner: { display: 'flex', alignItems: 'center', background: '#1a1500', border: '1px solid #78350f', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' },
  setupBtn:     { marginLeft: 'auto', background: 'none', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 },
  section:      { marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a3040' },
  sectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', marginBottom: '10px' },
  zoneRow:      { display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  zoneBox:      { flex: '1 0 56px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', padding: '8px 4px', textAlign: 'center' },
  zoneTemp:     { fontSize: '15px', fontWeight: '700', color: '#f59e0b' },
  zoneLabel:    { fontSize: '8px', color: '#6b7280', marginTop: '3px' },
  grid2:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  prow:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #2a3040' },
  prowLabel:    { fontSize: '12px', color: '#6b7280' },
  prowValue:    { fontSize: '12px', fontWeight: '600' },
  prowNote:     { fontSize: '10px', color: '#4b5563' },
  stageTable:   { marginTop: '8px', background: '#0f1117', borderRadius: '6px', overflow: 'hidden' },
  stageRow:     { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderBottom: '1px solid #2a3040' },
  stageBadge:   { fontSize: '9px', fontWeight: '700', background: '#2a3040', color: '#9ca3af', padding: '2px 5px', borderRadius: '3px' },
  stageVal:     { fontSize: '12px', fontWeight: '600', minWidth: '35px' },
  stageMeta:    { fontSize: '11px', color: '#6b7280' },
  cycleRow:     { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  cycleItem:    { flex: '1 0 60px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', padding: '10px 6px', textAlign: 'center' },
  cycleVal:     { fontSize: '18px', fontWeight: '700' },
  cycleLabel:   { fontSize: '10px', color: '#6b7280', marginTop: '3px' },
};

const hs = {
  statsRow:    { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '4px' },
  statBox:     { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px', textAlign: 'center' },
  statVal:     { fontSize: '20px', fontWeight: '700', color: '#f0f2f5', marginBottom: '4px' },
  statLabel:   { fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' },
  jobRow:      { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #2a3040' },
  jobDate:     { flexShrink: 0, minWidth: '72px' },
  jobDateMain: { fontSize: '12px', fontWeight: '600' },
  jobDateSub:  { fontSize: '10px', color: '#6b7280' },
  jobMachine:  { fontSize: '12px', fontWeight: '600' },
  jobMaterial: { fontSize: '11px', color: '#6b7280' },
  jobDur:      { fontSize: '11px', color: '#6b7280' },
  goldenBadge: { fontSize: '10px', background: '#451a03', color: '#f59e0b', border: '1px solid #78350f', padding: '2px 7px', borderRadius: '4px', display: 'flex', alignItems: 'center' },
};

const s = {
  page:            { padding: '20px', fontFamily: 'system-ui, sans-serif', background: '#0f1117', minHeight: '100vh', color: '#f0f2f5' },
  center:          { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#6b7280' },
  addBtn:          { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  emptyCard:       { textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: '13px' },
  emptyTab:        { textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: '13px' },
  breadcrumb:      { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  backBtn:         { background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' },
  breadcrumbSep:   { color: '#2a3040' },
  breadcrumbItem:  { fontSize: '12px', color: '#6b7280' },
  parentLink:      { fontSize: '11px', color: '#14b8a6', marginBottom: '4px' },
  detailHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  detailRef:       { fontSize: '11px', fontWeight: '700', color: '#14b8a6', fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: '4px' },
  detailName:      { fontSize: '24px', fontWeight: '700', margin: '0' },
  detailCustomer:  { fontSize: '13px', color: '#6b7280', marginTop: '4px' },
  detailDescription: { fontSize: '13px', color: '#9ca3af', marginTop: '8px', lineHeight: '1.5', maxWidth: '560px' },
  editBtn:         { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 },
  cardsGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(max(320px, calc((100% - 32px) / 3)), 1fr))', gap: '16px', alignItems: 'stretch', marginBottom: '16px' },
  statStrip:       { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' },
  statTile:        { flex: '1 1 0', minWidth: 0, background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 6px', textAlign: 'center' },
  statTileVal:     { fontSize: '14px', whiteSpace: 'nowrap', fontWeight: '700', color: '#f0f2f5' },
  statTileLabel:   { fontSize: '10px', color: '#6b7280', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  card:            { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '16px' },
  cardTitle:       { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#14b8a6', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #2a3040' },
  infoRow:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #2a304030' },
  infoLabel:       { fontSize: '12px', color: '#6b7280', flexShrink: 0, marginRight: '16px' },
  infoValue:       { fontSize: '13px', fontWeight: '600', textAlign: 'right' },
  reqSection:      { marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #2a304030' },
  reqLabel:        { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '4px' },
  reqValue:        { fontSize: '13px', lineHeight: '1.6', color: '#f0f2f5' },
  recipeCard:      { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px', marginBottom: '8px' },
  recipeHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  recipeName:      { fontSize: '13px', fontWeight: '700' },
  primaryBadge:    { fontSize: '9px', background: '#14b8a6', color: '#fff', padding: '2px 6px', borderRadius: '4px' },
  recipeMat:       { fontSize: '12px', color: '#6b7280', marginBottom: '6px' },
  recipeComp:      { fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' },
  compBadge:       { fontSize: '8px', fontWeight: '700', padding: '1px 4px', borderRadius: '3px', color: '#fff' },
  sectionTitle:    { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', marginBottom: '12px' },
  varGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
  varCard:         { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '12px', cursor: 'pointer' },
  varRef:          { fontSize: '10px', fontWeight: '700', color: '#14b8a6', fontFamily: 'monospace' },
  varName:         { fontSize: '13px', fontWeight: '700', marginTop: '4px' },
};
